import { DialogWithForm } from '@strata/react-dialog';
import { NumberInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestMultiSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { handleError } from 'services/Error';
import {
    useGetBatchesQuery,
    useIncrementalBatchReleaseMutation,
    useReprocessTreatmentMutation,
} from 'store/api/goLiveApi';

export const ReleaseBatchDialog = ({ isOpen, onClose }) => {
    const [reprocessTreatment] = useReprocessTreatmentMutation();
    const [releaseBatchInIncrements] = useIncrementalBatchReleaseMutation();
    const { data: batchOptions } = useGetBatchesQuery();

    const incrementalReleaseOption = 'With delays';

    return (
        <Formik
            initialValues={{
                cadence: incrementalReleaseOption,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                batches: Yup.array()
                    .of(Yup.object().shape({ batchId: Yup.string(), name: Yup.string() }))
                    .required(validationStrings.requiredField.errorMessage),
                cadence: Yup.string().required(validationStrings.requiredField.errorMessage),
                quantityPerRelease: Yup.number().when('cadence', {
                    is: (cadence) => cadence === incrementalReleaseOption,
                    then: (schema) =>
                        schema
                            .min(1, 'At least one treatment must be released at a time')
                            .max(5000, 'Maximum 5000 treatments released at once')
                            .required(validationStrings.requiredField.errorMessage),
                }),
                timeInterval: Yup.number().when('cadence', {
                    is: (cadence) => cadence === incrementalReleaseOption,
                    then: (schema) =>
                        schema
                            .min(1, 'The minumum interval is one minute')
                            .max(60, 'Intervals may not be longer than one hour')
                            .required(validationStrings.requiredField.errorMessage),
                }),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    if (values.cadence === incrementalReleaseOption) {
                        await releaseBatchInIncrements({
                            batchIds: values.batches.map((b) => b.batchId),
                            timeInterval: values.timeInterval,
                            quantityPerRelease: values.quantityPerRelease,
                        });
                    } else {
                        await reprocessTreatment({
                            upcomingTreatmentBatchName: values.batches.map((b) => b.name),
                        }).unwrap();
                    }
                    SuccessToast({ message: 'Batch released successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to release batch. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Release a Batch"
                    isOpen={isOpen}
                    width={560}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                    }}
                    primaryButtonLabel="Release"
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    dialogContent={
                        <>
                            <SuggestMultiSelectFormik
                                id="batches"
                                label="Batch(es)"
                                values={batchOptions}
                                required
                            />
                            <RadioButtonGroupFormik
                                name="cadence"
                                label="Cadence"
                                options={['All at once', incrementalReleaseOption]}
                                required
                            />
                            {formik.values.cadence === incrementalReleaseOption && (
                                <div className="flex__gap--small" style={{ width: '400px' }}>
                                    <span className="sds_upcoming-treatments__release-batch-dialog--input-text">
                                        In sets of
                                    </span>
                                    <NumberInputFormik
                                        id="quantityPerRelease"
                                        step={1}
                                        maxDecimalPlaces={0}
                                        required
                                    />
                                    <span className="sds_upcoming-treatments__release-batch-dialog--input-text">
                                        with
                                    </span>
                                    <NumberInputFormik
                                        id="timeInterval"
                                        step={1}
                                        maxDecimalPlaces={0}
                                        required
                                    />
                                    <span className="sds_upcoming-treatments__release-batch-dialog--input-text">
                                        minute delays
                                    </span>
                                </div>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

ReleaseBatchDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
